import axios from "axios";
import { useAuthStore } from "@/stores/auth";
import i18n from "@/i18n";

// Default to a relative path so the SPA works on whatever host it is served
// from (mavinty.com today, app.mavinty.com after the DNS/nginx cutover).
// VITE_API_BASE_URL can override this — useful for dev against a remote
// backend, or for embedding the SPA on a different origin.
const API_BASE = import.meta.env.VITE_API_BASE_URL || "/api";

export const api = axios.create({
  baseURL: API_BASE,
  timeout: 120000,
});

api.interceptors.request.use((config) => {
  const token = useAuthStore.getState().token;
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  config.headers["Accept-Language"] = i18n.language || "en";
  return config;
});

/** Why the paywall turned the athlete away, stashed for /pricing to explain. */
export const PAYWALL_REASON_KEY = "mavinty_paywall_reason";

api.interceptors.response.use(
  (r) => r,
  (err) => {
    const status = err.response?.status;

    if (status === 401) {
      useAuthStore.getState().logout();
      if (typeof window !== "undefined" && !window.location.pathname.startsWith("/login")) {
        window.location.href = "/login";
      }
    }

    // 402 — signed in, but the subscription lapsed or was never started. The
    // backend whitelists /billing, /profile, /auth and /i18n, so /pricing can
    // still load and explain itself; everything else in the app answers 402
    // until there is a live subscription. Without this branch the athlete just
    // sees requests failing with no explanation and no way forward.
    if (status === 402 && typeof window !== "undefined") {
      const reason = err.response?.data?.detail?.reason ?? "no_subscription";
      sessionStorage.setItem(PAYWALL_REASON_KEY, String(reason));
      if (!window.location.pathname.startsWith("/pricing")) {
        window.location.href = "/pricing";
      }
    }

    return Promise.reject(err);
  },
);
